import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertCircle,
  AlertTriangle,
  Barcode,
  Braces,
  Copy,
  Download,
  Grid3x3,
  Image as ImageIcon,
  Loader2,
  Minus,
  Printer,
  QrCode,
  Redo2,
  RefreshCw,
  Save,
  Square,
  Type,
  Undo2,
  X,
} from "lucide-react";

import { alert } from "@/contexts/AlertModalContext";
import { useSocket } from "@/contexts/SocketContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";

import type { ListaZplItem } from "@/types/lista-zpl-item";
import type {
  ElementoImagem,
  EtiquetaDesign,
  EtiquetaElemento,
} from "@/types/etiqueta-designer";
import { criarDesignVazio } from "@/types/etiqueta-designer";
import {
  criarElemento,
  dimensoesElemento,
  dpmmDe,
  gerarZpl,
  importarZpl,
  mmParaDots,
  novoId,
  redimensionarBitmap,
  renderizarPreview,
  separarDownloadsGraficos,
  bitmapDeElemento,
  elementoImagemDeBitmap,
  type ContextoEtiqueta,
} from "@/utils/zplDesigner";
import { preencherVariaveisExemplo } from "@/utils/variaveisZpl";

import { CanvasEtiqueta } from "./CanvasEtiqueta";
import { PainelCamadas } from "./PainelCamadas";
import { PainelPropriedades } from "./PainelPropriedades";
import { DialogoImagem } from "./DialogoImagem";

const PRESETS = [
  { rotulo: "100 × 50 mm", largura: "100", altura: "50" },
  { rotulo: "100 × 150 mm", largura: "100", altura: "150" },
  { rotulo: "60 × 40 mm", largura: "60", altura: "40" },
  { rotulo: "50 × 25 mm", largura: "50", altura: "25" },
  { rotulo: "33 × 22 mm", largura: "33", altura: "22" },
];

type Props = {
  valorInicial?: ListaZplItem | null;
  /** nomes já usados nas outras etiquetas, para impedir duplicidade */
  nomesExistentes: string[];
  salvando?: boolean;
  onSalvar: (item: ListaZplItem) => void;
  onCancelar: () => void;
  /** espelha o estado atual pro editor simples continuar de onde parou */
  onRascunho?: (item: ListaZplItem) => void;
};

export function EtiquetaDesigner({
  valorInicial,
  nomesExistentes,
  salvando,
  onSalvar,
  onCancelar,
  onRascunho,
}: Props) {
  const { enviarMensagem, isConnected } = useSocket();
  const { impressaoConfig } = useImpressaoConfig();

  const [nome, setNome] = useState(valorInicial?.nome ?? "");
  const [largura, setLargura] = useState(valorInicial?.largura ?? "100");
  const [altura, setAltura] = useState(valorInicial?.altura ?? "50");
  const [dpi, setDpi] = useState(valorInicial?.dpmm ?? "203");

  const ctx: ContextoEtiqueta = useMemo(
    () => ({
      larguraMm: Number(largura) || 1,
      alturaMm: Number(altura) || 1,
      dpmm: dpmmDe(dpi),
    }),
    [largura, altura, dpi],
  );

  // ---- design + histórico ----
  const designInicial = useMemo<EtiquetaDesign>(() => {
    if (valorInicial?.design?.elementos) return valorInicial.design;
    if (valorInicial?.zpl?.trim()) {
      return importarZpl(valorInicial.zpl, {
        larguraMm: Number(valorInicial.largura) || 100,
        alturaMm: Number(valorInicial.altura) || 50,
        dpmm: dpmmDe(valorInicial.dpmm || "203"),
      }).design;
    }
    return criarDesignVazio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [design, setDesign] = useState<EtiquetaDesign>(designInicial);
  const [historico, setHistorico] = useState<{
    pilha: EtiquetaDesign[];
    pos: number;
  }>({ pilha: [designInicial], pos: 0 });
  const designRef = useRef(design);
  designRef.current = design;

  const registrar = useCallback(
    (novo: EtiquetaDesign) => {
      setDesign(novo);
      const pilha = [
        ...historico.pilha.slice(0, historico.pos + 1),
        novo,
      ].slice(-60);
      setHistorico({ pilha, pos: pilha.length - 1 });
    },
    [historico],
  );

  const desfazer = useCallback(() => {
    if (historico.pos <= 0) return;
    setDesign(historico.pilha[historico.pos - 1]);
    setHistorico({ ...historico, pos: historico.pos - 1 });
  }, [historico]);

  const refazer = useCallback(() => {
    if (historico.pos >= historico.pilha.length - 1) return;
    setDesign(historico.pilha[historico.pos + 1]);
    setHistorico({ ...historico, pos: historico.pos + 1 });
  }, [historico]);

  // ---- seleção / canvas ----
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(6);
  const [grade, setGrade] = useState(true);
  const [snap, setSnap] = useState(true);
  const zoomAjustado = useRef(false);

  useEffect(() => {
    if (zoomAjustado.current) return;
    zoomAjustado.current = true;
    setZoom(
      Math.min(14, Math.max(2, Math.round(620 / (Number(largura) || 100)))),
    );
  }, [largura]);

  const selecionado = useMemo(
    () => design.elementos.find((e) => e.id === selecionadoId) ?? null,
    [design.elementos, selecionadoId],
  );

  // ---- ZPL ----
  const [zplManual, setZplManual] = useState(!!valorInicial?.zpl_manual);
  const [zplTexto, setZplTexto] = useState(valorInicial?.zpl ?? "");

  const zplGerado = useMemo(() => gerarZpl(design, ctx), [design, ctx]);
  const zplFinal = zplManual ? zplTexto : zplGerado;

  useEffect(() => {
    if (!zplManual) setZplTexto(zplGerado);
  }, [zplGerado, zplManual]);

  useEffect(() => {
    onRascunho?.({
      nome: nome.trim(),
      largura,
      altura,
      dpmm: dpi,
      zpl: zplFinal,
      design,
      zpl_manual: zplManual || undefined,
    });
  }, [
    nome,
    largura,
    altura,
    dpi,
    zplFinal,
    design,
    zplManual,
    onRascunho,
  ]);

  // ---- pré-visualização real (Labelary) ----
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewErro, setPreviewErro] = useState<string | null>(null);
  const [previewCarregando, setPreviewCarregando] = useState(false);
  const [abaDireita, setAbaDireita] = useState("propriedades");

  const atualizarPreview = useCallback(
    async (sinal?: AbortSignal) => {
      setPreviewCarregando(true);
      setPreviewErro(null);
      const resultado = await renderizarPreview(
        preencherVariaveisExemplo(zplFinal),
        ctx,
        sinal,
      );
      if (sinal?.aborted) return;
      setPreviewCarregando(false);
      if (resultado.ok) {
        setPreviewUrl((antigo) => {
          if (antigo) URL.revokeObjectURL(antigo);
          return resultado.url;
        });
      } else if (resultado.erro) {
        setPreviewErro(resultado.erro);
      }
    },
    [zplFinal, ctx],
  );

  useEffect(() => {
    if (abaDireita !== "previa") return;
    const controlador = new AbortController();
    const t = setTimeout(() => atualizarPreview(controlador.signal), 700);
    return () => {
      clearTimeout(t);
      controlador.abort();
    };
  }, [abaDireita, atualizarPreview]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // ---- imagem ----
  const [dialogoImagem, setDialogoImagem] = useState(false);
  const [imagemAlvo, setImagemAlvo] = useState<ElementoImagem | null>(null);

  // ---- mutações ----
  const substituirElemento = useCallback(
    (id: string, patch: Record<string, unknown>, comHistorico = true) => {
      const atual = designRef.current;
      const elementos = atual.elementos.map((el) => {
        if (el.id !== id) return el;

        // redimensionamento de imagem precisa reamostrar o bitmap
        if (patch.__redimensionarImagem && el.tipo === "imagem") {
          const pedido = patch.__redimensionarImagem as {
            largura?: number;
            altura?: number;
            manterProporcao?: boolean;
          };
          const proporcao = el.alturaDots / el.larguraDots;
          let w = pedido.largura ?? el.larguraDots;
          let h = pedido.altura ?? el.alturaDots;
          if (pedido.manterProporcao) {
            if (pedido.largura !== undefined) h = Math.round(w * proporcao);
            else if (pedido.altura !== undefined)
              w = Math.round(h / (proporcao || 1));
          }
          const bmp = redimensionarBitmap(
            bitmapDeElemento(el),
            Math.max(1, w),
            Math.max(1, h),
          );
          return { ...el, ...elementoImagemDeBitmap(bmp) };
        }

        const limpo = { ...patch };
        delete limpo.__redimensionarImagem;
        return { ...el, ...limpo } as EtiquetaElemento;
      });
      const novo = { ...atual, elementos };
      if (comHistorico) registrar(novo);
      else setDesign(novo);
    },
    [registrar],
  );

  const adicionar = (tipo: Parameters<typeof criarElemento>[0]) => {
    const el = criarElemento(tipo, ctx);
    registrar({ ...design, elementos: [...design.elementos, el] });
    setSelecionadoId(el.id);
    setAbaDireita("propriedades");
  };

  const excluir = useCallback(
    (id: string) => {
      registrar({
        ...designRef.current,
        elementos: designRef.current.elementos.filter((e) => e.id !== id),
      });
      setSelecionadoId((s) => (s === id ? null : s));
    },
    [registrar],
  );

  const duplicar = useCallback(
    (id: string) => {
      const alvo = designRef.current.elementos.find((e) => e.id === id);
      if (!alvo) return;
      const copia = { ...alvo, id: novoId(), x: alvo.x + 2, y: alvo.y + 2 };
      registrar({
        ...designRef.current,
        elementos: [...designRef.current.elementos, copia],
      });
      setSelecionadoId(copia.id);
    },
    [registrar],
  );

  const moverOrdem = (id: string, direcao: -1 | 1) => {
    const elementos = [...design.elementos];
    const i = elementos.findIndex((e) => e.id === id);
    const j = i + direcao;
    if (i < 0 || j < 0 || j >= elementos.length) return;
    [elementos[i], elementos[j]] = [elementos[j], elementos[i]];
    registrar({ ...design, elementos });
  };

  const centralizar = (eixo: "h" | "v") => {
    if (!selecionado) return;
    const dim = dimensoesElemento(selecionado, ctx);
    if (eixo === "h") {
      substituirElemento(selecionado.id, {
        x: Math.round(((ctx.larguraMm - dim.largura) / 2) * 10) / 10,
      });
    } else {
      substituirElemento(selecionado.id, {
        y: Math.round(((ctx.alturaMm - dim.altura) / 2) * 10) / 10,
      });
    }
  };

  const redimensionarPorHandle = useCallback(
    (id: string, larguraMm: number, alturaMm: number) => {
      const el = designRef.current.elementos.find((e) => e.id === id);
      if (!el) return;
      const dim = dimensoesElemento(el, ctx);
      switch (el.tipo) {
        case "texto": {
          const linhas = Math.max((el.texto || " ").split("\n").length, 1);
          const novaAltura = Math.max(0.8, alturaMm / linhas);
          const fatorLargura = dim.largura > 0 ? larguraMm / dim.largura : 1;
          substituirElemento(
            id,
            {
              alturaFonte: Math.round(novaAltura * 10) / 10,
              larguraFonte:
                Math.round(
                  Math.max(0.8, el.larguraFonte * fatorLargura) * 10,
                ) / 10,
            },
            false,
          );
          break;
        }
        case "barras": {
          const digitos = Math.max((el.dados || "").length, 1);
          const moduloAlvo = Math.round(
            mmParaDots(larguraMm, ctx.dpmm) / (digitos * 11),
          );
          substituirElemento(
            id,
            {
              altura: Math.max(
                2,
                Math.round((alturaMm - (el.mostrarTexto ? 3 : 0)) * 10) / 10,
              ),
              modulo: Math.min(10, Math.max(1, moduloAlvo || el.modulo)),
            },
            false,
          );
          break;
        }
        case "qrcode": {
          const lado = Math.min(larguraMm, alturaMm);
          const mag = Math.round(mmParaDots(lado, ctx.dpmm) / 25);
          substituirElemento(
            id,
            { magnificacao: Math.min(10, Math.max(1, mag || 1)) },
            false,
          );
          break;
        }
        case "imagem": {
          substituirElemento(
            id,
            {
              __redimensionarImagem: {
                largura: Math.max(1, mmParaDots(larguraMm, ctx.dpmm)),
                altura: Math.max(1, mmParaDots(alturaMm, ctx.dpmm)),
              },
            },
            false,
          );
          break;
        }
        case "caixa":
        case "linha": {
          substituirElemento(
            id,
            { largura: larguraMm, altura: alturaMm },
            false,
          );
          break;
        }
        default:
          break;
      }
    },
    [ctx, substituirElemento],
  );

  const mover = useCallback(
    (id: string, x: number, y: number) =>
      substituirElemento(id, { x, y }, false),
    [substituirElemento],
  );

  const finalizarArrasto = useCallback(() => {
    registrar(designRef.current);
  }, [registrar]);

  // ---- atalhos ----
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement;
      const digitando =
        alvo?.tagName === "INPUT" ||
        alvo?.tagName === "TEXTAREA" ||
        alvo?.isContentEditable;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) refazer();
        else desfazer();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        refazer();
        return;
      }
      if (digitando || !selecionadoId) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
        e.preventDefault();
        duplicar(selecionadoId);
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        excluir(selecionadoId);
        return;
      }
      const passo = e.shiftKey ? 1 : 0.1;
      const el = designRef.current.elementos.find(
        (x) => x.id === selecionadoId,
      );
      if (!el) return;
      const deslocar = (dx: number, dy: number) => {
        e.preventDefault();
        substituirElemento(selecionadoId, {
          x: Math.round((el.x + dx) * 100) / 100,
          y: Math.round((el.y + dy) * 100) / 100,
        });
      };
      if (e.key === "ArrowLeft") deslocar(-passo, 0);
      if (e.key === "ArrowRight") deslocar(passo, 0);
      if (e.key === "ArrowUp") deslocar(0, -passo);
      if (e.key === "ArrowDown") deslocar(0, passo);
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [selecionadoId, desfazer, refazer, duplicar, excluir, substituirElemento]);

  // ---- ações de topo ----
  const copiarZpl = async () => {
    try {
      await navigator.clipboard.writeText(zplFinal);
      alert.success("ZPL copiado.");
    } catch {
      alert.error("Não foi possível copiar.");
    }
  };

  const baixarZpl = () => {
    const blob = new Blob([zplFinal], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(nome || "etiqueta").replace(/[^\w-]+/g, "_")}.zpl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const imprimirTeste = () => {
    if (!isConnected) {
      alert.error("Impressora não conectada.");
      return;
    }
    const delay = impressaoConfig?.tempo_delay_gravacao || "720";
    const tentativas = impressaoConfig?.tentativas_gravacao || "1";
    const forca = impressaoConfig?.potencia_padrao || "2";
    const inicioVirgem = impressaoConfig?.inicio_tag_virgem || "E280";
    const maiorEpc = impressaoConfig?.tag_virgem_maior_epc ? "true" : "false";
    // pacote "-" imprime sem gravar RFID (mesmo caminho da marcação de defeito)
    const empacotar = (parte: string) =>
      `-${parte}|false|FFFFFFFFFFFFFFFFFFFFFFFF|${delay}|false|false|-|false|${tentativas}|false|${inicioVirgem}|${maiorEpc}|${forca}|true`;

    // ~DG/~DY tem que rodar fora do formato, antes do ^XA
    const { downloads, corpo } = separarDownloadsGraficos(
      preencherVariaveisExemplo(zplFinal),
    );
    const conteudo = corpo
      .replace(/\^XA/g, "")
      .replace(/\^XZ/g, "")
      .trim();

    enviarMensagem("FL");
    enviarMensagem(`P:${forca}`);
    for (let i = 0; i < downloads.length; i += 500) {
      enviarMensagem(empacotar(downloads.slice(i, i + 500)));
    }
    enviarMensagem(empacotar("^XA"));
    for (let i = 0; i < conteudo.length; i += 500) {
      enviarMensagem(empacotar(conteudo.slice(i, i + 500)));
    }
    enviarMensagem(empacotar("^XZ"));
    alert.success("Etiqueta de teste enviada à impressora.");
  };

  const importarParaVisual = () => {
    const resultado = importarZpl(zplTexto, ctx);
    registrar(resultado.design);
    setZplManual(false);
    setSelecionadoId(null);
    if (resultado.avisos.length) alert.warning(resultado.avisos.join(" "));
    else alert.success("ZPL importado para o editor visual.");
  };

  const salvar = () => {
    const nomeLimpo = nome.trim();
    if (!nomeLimpo) return alert.error("Informe uma descrição para a etiqueta.");
    if (!Number(largura) || Number(largura) <= 0)
      return alert.error("Largura deve ser maior que zero.");
    if (!Number(altura) || Number(altura) <= 0)
      return alert.error("Altura deve ser maior que zero.");
    if (!zplFinal.trim()) return alert.error("A etiqueta está vazia.");
    if (
      nomesExistentes.some(
        (n) => n.toLowerCase() === nomeLimpo.toLowerCase(),
      ) &&
      nomeLimpo.toLowerCase() !== (valorInicial?.nome ?? "").toLowerCase()
    )
      return alert.error("Já existe uma etiqueta com esse nome.");

    onSalvar({
      nome: nomeLimpo,
      largura: String(largura),
      altura: String(altura),
      dpmm: dpi,
      zpl: zplFinal,
      design,
      zpl_manual: zplManual || undefined,
    });
  };

  const pesoZplKb = Math.round(zplFinal.length / 1024);

  return (
    <div className="space-y-3">
      {/* ---- cabeçalho ---- */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5 min-w-[220px] flex-1">
              <Label htmlFor="et-nome" className="text-[11px] text-muted-foreground">
                Descrição <span className="text-destructive">*</span>
              </Label>
              <Input
                id="et-nome"
                className="h-9"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Etiqueta Caixa 100x50"
              />
            </div>

            <div className="grid gap-1.5 w-28">
              <Label htmlFor="et-larg" className="text-[11px] text-muted-foreground">
                Largura (mm)
              </Label>
              <Input
                id="et-larg"
                className="h-9"
                type="number"
                min="1"
                value={largura}
                onChange={(e) => setLargura(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5 w-28">
              <Label htmlFor="et-alt" className="text-[11px] text-muted-foreground">
                Altura (mm)
              </Label>
              <Input
                id="et-alt"
                className="h-9"
                type="number"
                min="1"
                value={altura}
                onChange={(e) => setAltura(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5 w-24">
              <Label className="text-[11px] text-muted-foreground">DPI</Label>
              <Select value={dpi} onValueChange={setDpi}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="203">203</SelectItem>
                  <SelectItem value="300">300</SelectItem>
                  <SelectItem value="600">600</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5 w-40">
              <Label className="text-[11px] text-muted-foreground">
                Tamanho comum
              </Label>
              <Select
                value=""
                onValueChange={(v) => {
                  const p = PRESETS.find((x) => x.rotulo === v);
                  if (!p) return;
                  setLargura(p.largura);
                  setAltura(p.altura);
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Selecionar" />
                </SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => (
                    <SelectItem key={p.rotulo} value={p.rotulo}>
                      {p.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onCancelar}>
                <X className="h-4 w-4" />
                Cancelar
              </Button>
              <Button size="sm" onClick={salvar} disabled={salvando}>
                {salvando ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Salvar etiqueta
              </Button>
            </div>
          </div>

          <Separator />

          {/* ---- barra de ferramentas ---- */}
          <TooltipProvider delayDuration={300}>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => adicionar("texto")}>
                  <Type className="h-4 w-4" />
                  Texto
                </Button>
                <Button size="sm" variant="outline" onClick={() => adicionar("barras")}>
                  <Barcode className="h-4 w-4" />
                  Barras
                </Button>
                <Button size="sm" variant="outline" onClick={() => adicionar("qrcode")}>
                  <QrCode className="h-4 w-4" />
                  QR
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setImagemAlvo(null);
                    setDialogoImagem(true);
                  }}
                >
                  <ImageIcon className="h-4 w-4" />
                  Imagem
                </Button>
                <Button size="sm" variant="outline" onClick={() => adicionar("caixa")}>
                  <Square className="h-4 w-4" />
                  Caixa
                </Button>
                <Button size="sm" variant="outline" onClick={() => adicionar("linha")}>
                  <Minus className="h-4 w-4" />
                  Linha
                </Button>
                <Button size="sm" variant="outline" onClick={() => adicionar("bruto")}>
                  <Braces className="h-4 w-4" />
                  ZPL
                </Button>
              </div>

              <Separator orientation="vertical" className="h-6" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    onClick={desfazer}
                    disabled={historico.pos <= 0}
                  >
                    <Undo2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Desfazer (Ctrl+Z)</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    onClick={refazer}
                    disabled={historico.pos >= historico.pilha.length - 1}
                  >
                    <Redo2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Refazer (Ctrl+Shift+Z)</TooltipContent>
              </Tooltip>

              <Separator orientation="vertical" className="h-6" />

              <div className="flex items-center gap-2">
                <Grid3x3 className="h-4 w-4 text-muted-foreground" />
                <Switch checked={grade} onCheckedChange={setGrade} />
                <span className="text-[11px] text-muted-foreground">Grade</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={snap} onCheckedChange={setSnap} />
                <span className="text-[11px] text-muted-foreground">
                  Encaixe 0,5 mm
                </span>
              </div>

              <div className="flex items-center gap-2 min-w-[160px]">
                <span className="text-[11px] text-muted-foreground">Zoom</span>
                <Slider
                  min={2}
                  max={16}
                  step={1}
                  value={[zoom]}
                  onValueChange={([v]) => setZoom(v)}
                  className="w-24"
                />
              </div>

              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={copiarZpl}>
                  <Copy className="h-4 w-4" />
                  Copiar ZPL
                </Button>
                <Button size="sm" variant="outline" onClick={baixarZpl}>
                  <Download className="h-4 w-4" />
                  .zpl
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={imprimirTeste}
                  disabled={!isConnected}
                  title={
                    isConnected
                      ? "Imprime uma etiqueta de teste sem gravar RFID"
                      : "Impressora desconectada"
                  }
                >
                  <Printer className="h-4 w-4" />
                  Teste
                </Button>
              </div>
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>

      {zplManual && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-medium">Modo ZPL manual ativo.</p>
            <p>
              O que será salvo é o ZPL da aba <strong>ZPL</strong>. As alterações
              no editor visual não entram no arquivo enquanto este modo estiver
              ligado.
            </p>
          </div>
        </div>
      )}

      {/* ---- área de trabalho ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_340px] gap-3">
        {/* camadas */}
        <Card className="overflow-hidden">
          <div className="border-b px-3 py-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Camadas ({design.elementos.length})
            </p>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            <PainelCamadas
              elementos={design.elementos}
              selecionadoId={selecionadoId}
              onSelecionar={(id) => {
                setSelecionadoId(id);
                setAbaDireita("propriedades");
              }}
              onPatch={(id, patch) => substituirElemento(id, patch)}
              onExcluir={excluir}
              onMoverOrdem={moverOrdem}
            />
          </div>
        </Card>

        {/* canvas */}
        <div className="space-y-2">
          <CanvasEtiqueta
            design={design}
            ctx={ctx}
            zoom={zoom}
            grade={grade}
            snap={snap}
            selecionadoId={selecionadoId}
            onSelecionar={setSelecionadoId}
            onMover={mover}
            onRedimensionar={redimensionarPorHandle}
            onFinalizarMover={finalizarArrasto}
          />
          <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            <span>
              {ctx.larguraMm} × {ctx.alturaMm} mm ·{" "}
              {mmParaDots(ctx.larguraMm, ctx.dpmm)} ×{" "}
              {mmParaDots(ctx.alturaMm, ctx.dpmm)} dots · {dpi} DPI
            </span>
            {selecionado && (
              <Badge variant="secondary" className="font-normal">
                {selecionado.tipo} · X {selecionado.x} mm / Y {selecionado.y} mm
              </Badge>
            )}
            <span className="ml-auto">ZPL {pesoZplKb || "<1"} KB</span>
          </div>
        </div>

        {/* painel direito */}
        <Card className="overflow-hidden">
          <Tabs value={abaDireita} onValueChange={setAbaDireita}>
            <TabsList className="w-full rounded-none border-b bg-transparent p-0">
              <TabsTrigger value="propriedades" className="flex-1 text-xs">
                Propriedades
              </TabsTrigger>
              <TabsTrigger value="zpl" className="flex-1 text-xs">
                ZPL
              </TabsTrigger>
              <TabsTrigger value="previa" className="flex-1 text-xs">
                Prévia real
              </TabsTrigger>
            </TabsList>

            <TabsContent value="propriedades" className="m-0 max-h-[560px] overflow-y-auto">
              <PainelPropriedades
                elemento={selecionado}
                ctx={ctx}
                onPatch={(patch) =>
                  selecionado && substituirElemento(selecionado.id, patch)
                }
                onExcluir={() => selecionado && excluir(selecionado.id)}
                onDuplicar={() => selecionado && duplicar(selecionado.id)}
                onSubir={() => selecionado && moverOrdem(selecionado.id, 1)}
                onDescer={() => selecionado && moverOrdem(selecionado.id, -1)}
                onCentralizar={centralizar}
                onTrocarImagem={() => {
                  if (selecionado?.tipo === "imagem") {
                    setImagemAlvo(selecionado);
                    setDialogoImagem(true);
                  }
                }}
              />
            </TabsContent>

            <TabsContent value="zpl" className="m-0 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    id="zpl-manual"
                    checked={zplManual}
                    onCheckedChange={(v) => {
                      setZplManual(v);
                      if (v) setZplTexto(zplFinal);
                    }}
                  />
                  <Label htmlFor="zpl-manual" className="text-xs">
                    Editar manualmente
                  </Label>
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {zplFinal.length.toLocaleString("pt-BR")} caracteres
                </span>
              </div>
              <Textarea
                className="h-[420px] text-[11px] font-mono resize-none"
                value={zplFinal}
                readOnly={!zplManual}
                onChange={(e) => setZplTexto(e.target.value)}
                spellCheck={false}
              />
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={importarParaVisual}
              >
                Importar este ZPL para o editor visual
              </Button>
              <p className="text-[10px] text-muted-foreground">
                A importação converte comandos reconhecidos em elementos
                editáveis. O que não for reconhecido vira um bloco “ZPL bruto”,
                preservado como está.
              </p>
            </TabsContent>

            <TabsContent value="previa" className="m-0 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground">
                  Renderizado pela Labelary com valores de exemplo.
                </p>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => atualizarPreview()}
                  disabled={previewCarregando}
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${previewCarregando ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
              <div className="relative flex min-h-[300px] items-center justify-center overflow-auto rounded-md border bg-white p-2">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Pré-visualização da etiqueta"
                    className="max-w-full"
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {previewCarregando ? "Renderizando..." : "Sem pré-visualização."}
                  </p>
                )}
                {previewCarregando && previewUrl && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
              {previewErro && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-[11px] text-destructive dark:text-red-400">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span className="break-words">{previewErro}</span>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      <DialogoImagem
        aberto={dialogoImagem}
        onOpenChange={setDialogoImagem}
        ctx={ctx}
        elementoAtual={imagemAlvo}
        onConfirmar={(dados) => {
          if (imagemAlvo) {
            substituirElemento(imagemAlvo.id, dados);
          } else {
            const el: ElementoImagem = {
              id: novoId(),
              tipo: "imagem",
              x: 2,
              y: 2,
              rotacao: 0,
              ...dados,
            };
            registrar({ ...design, elementos: [...design.elementos, el] });
            setSelecionadoId(el.id);
          }
          setImagemAlvo(null);
        }}
      />
    </div>
  );
}
